// src/components/pdf-table/BulkActionToolbar.js
import React, { useState, useMemo, useEffect } from 'react';
import { Trash2, TriangleAlert, CircleHelp } from 'lucide-react';
import BulkDocumentSetSelector from './BulkDocumentSetSelector';
import DeleteAlertDialog from '../ui/DeleteAlertDialog';
import PopoverWrapper from '../ui/PopoverWrapper';
import { ReactComponent as TagAdd } from '../../assets/icons/tag-add.svg';
import { ReactComponent as TagRemove } from '../../assets/icons/tag-remove.svg';
import { trackBulkActionToolbarOpened } from '../../utils/analytics';
/**
 * Bulk Action Toolbar Component
 * Appears at bottom of PDF table when documents are selected
 */
const BulkActionToolbar = ({
  selectedCount = 0,
  selectedDocumentIds = [],
  documentSets = [],
  pdfFiles = [],
  onAddSetsToDocuments,
  onRemoveSetsFromDocuments,
  onDeleteDocuments,
  onCreateDocumentSet,
  onStartGuide,
}) => {
  const [isAddSetsOpen, setIsAddSetsOpen] = useState(false);
  const [isRemoveSetsOpen, setIsRemoveSetsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Track when toolbar opens (when selectedCount becomes > 0)
  useEffect(() => {
    if (selectedCount > 0) {
      trackBulkActionToolbarOpened(selectedCount);
    }
  }, [selectedCount]);

  // Calculate union of all sets used in selected documents (for Remove Sets)
  const unionSetIds = useMemo(() => {
    if (selectedCount === 0 || !pdfFiles) return [];
    
    const selectedDocs = pdfFiles.filter(file => 
      selectedDocumentIds.includes(file.id) && file.id !== 'INDEX_DOCUMENT'
    );
    
    if (selectedDocs.length === 0) return [];
    
    // Get unique set IDs from all selected documents (union)
    const allSetIds = new Set();
    selectedDocs.forEach(doc => {
      (doc.documentSets || []).forEach(setId => allSetIds.add(setId));
    });
    
    return Array.from(allSetIds);
  }, [selectedCount, selectedDocumentIds, pdfFiles]);

  const handleAddSets = (setIds) => {
    onAddSetsToDocuments?.(selectedDocumentIds, setIds);
    setIsAddSetsOpen(false);
  };

  const handleRemoveSets = (setIds) => {
    onRemoveSetsFromDocuments?.(selectedDocumentIds, setIds);
    setIsRemoveSetsOpen(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    onDeleteDocuments?.(selectedDocumentIds);
  };

  if (selectedCount === 0) return null;

  return (
    <div className="flex justify-center pointer-events-none">
      <div
        data-guide-toolbar
        className="w-full max-w-[360px] bg-app-gray-700 border border-app-gray-900 rounded-full px-1 py-app-sm flex items-center justify-evenly pointer-events-auto"
        style={{
          animation: 'slideUpFadeIn 200ms cubic-bezier(0.25, 1, 0.5, 1) forwards'
        }}
      >
        {/* Selected count */}
        <span className="text-white text-app-2xs">
          <span className="font-semibold">{selectedCount}</span> {selectedCount === 1 ? 'doc' : 'docs'}
        </span>

        {/* Divider */}
        <div className="w-px h-comp-lg bg-app-gray-600" />

        {/* Add Tags button with popover */}
        <PopoverWrapper
          trigger={
            <button
              data-guide-target="bulk-add-tags"
              className="group flex items-center gap-app-xs text-white text-app-2xs hover:text-app-gray-400"
            >
              <TagAdd className="w-[11px] h-[11px] text-white group-hover:text-app-success" />
              <span className="inline">Add Tags</span>
            </button>
          }
          open={isAddSetsOpen}
          onOpenChange={setIsAddSetsOpen}
          theme="dark"
          sideOffset={16}
          align="center"
          side="top"
          zIndex={100}
        >
          <BulkDocumentSetSelector
            mode="add"
            availableSets={documentSets}
            initialSelectedIds={[]}
            selectedCount={selectedCount}
            onSubmit={handleAddSets}
            onCreateSet={onCreateDocumentSet}
          />
        </PopoverWrapper>

        {/* Divider */}
        <div className="w-px h-comp-lg bg-app-gray-600" />

        {/* Remove Tags button with popover */}
        <PopoverWrapper
          trigger={
            <button
              data-guide-target="bulk-remove-tags"
              className="group flex items-center gap-app-xs text-white text-app-2xs hover:text-app-gray-400"
            >
              <TagRemove className="w-[11px] h-[11px] text-white group-hover:text-app-accent" />
              <span className="inline">Remove Tags</span>
            </button>
          }
          open={isRemoveSetsOpen}
          onOpenChange={setIsRemoveSetsOpen}
          theme="dark"
          sideOffset={16}
          align="center"
          side="top"
          zIndex={100}
        >
          <BulkDocumentSetSelector
            mode="remove"
            availableSets={documentSets.filter(set => unionSetIds.includes(set.id))}
            initialSelectedIds={unionSetIds}
            selectedCount={selectedCount}
            onSubmit={handleRemoveSets}
            onCreateSet={() => {}}  // Can't create in remove mode
          />
        </PopoverWrapper>

        {/* Divider */}
        <div className="w-px h-comp-lg bg-app-gray-600" />

        {/* Delete button */}
        <button
          data-guide-target="bulk-delete"
          onClick={handleDeleteClick}
          className="text-white text-app-2xs transition-colors duration-app-fast hover:text-app-accent flex items-center gap-app-xs"
        >
          <Trash2 size={12} />
        </button>

        {/* Divider */}
        <div className="w-px h-comp-lg bg-app-gray-600" />

        {/* Help button */}
        <button
          data-guide-help
          onClick={onStartGuide}
          className="text-white transition-colors duration-app-fast hover:text-app-gray-400 flex items-center"
        >
          <CircleHelp size={12} />
        </button>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteAlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title={`Delete ${selectedCount} Document${selectedCount > 1 ? 's' : ''}?`}
        warnings={[
          <>{selectedCount > 1 ? 'These documents' : 'This document'} will be <strong className="text-app-gray-700">deleted</strong>. This can't be undone.</>
        ]}
        showBullets={false}
        warningIcon={<TriangleAlert className="w-comp-5xl h-comp-5xl" />}
        warningIconColor="text-app-warning"
        confirmButtonText={`Delete ${selectedCount > 1 ? 'Documents' : 'Document'}`}
        confirmButtonIcon={<Trash2 className="w-comp-lg h-comp-lg" />}
        onConfirm={handleConfirmDelete}
      />

      {/* Add animation keyframes */}
      <style>{`
        @keyframes slideUpFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default BulkActionToolbar;